import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { PageHeader, StatCard, fmtEUR } from "@/components/Common";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const STATE_META = {
  counting:  { label: "In conteggio", cls: "bg-amber-100 text-amber-900 border-amber-300" },
  review:    { label: "In revisione", cls: "bg-blue-100 text-blue-900 border-blue-300" },
  completed: { label: "Completato",   cls: "bg-emerald-100 text-emerald-900 border-emerald-300" },
  cancelled: { label: "Annullato",    cls: "bg-neutral-200 text-neutral-700 border-neutral-300" },
};

const StateBadge = ({ s }) => {
  const m = STATE_META[s] || { label: s, cls: "bg-neutral-100 text-neutral-700 border-neutral-300" };
  return <span className={`inline-block px-2 py-0.5 rounded border text-xs ${m.cls}`}>{m.label}</span>;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

// ---------- HISTORY ----------
function HistoryList({ counts, onOpen, onNew, loading }) {
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-neutral-500">Storico inventari fisici</div>
        <Button data-testid="new-inventory-btn" onClick={onNew} className="bg-neutral-900 text-[#FDFBF7]">+ Nuovo inventario</Button>
      </div>
      <div className="border border-[#E2D9C8] rounded-lg bg-white overflow-x-auto">
        <table className="w-full text-sm zebra min-w-[900px]">
          <thead>
            <tr className="text-left label-eyebrow">
              <th className="p-3">Data</th>
              <th className="p-3">Nome</th>
              <th className="p-3">Stato</th>
              <th className="p-3">Aperto da</th>
              <th className="p-3">Chiuso da</th>
              <th className="p-3 text-right">Val. teorico</th>
              <th className="p-3 text-right">Val. reale</th>
              <th className="p-3 text-right">Diff €</th>
              <th className="p-3 text-right"># diff</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody data-testid="history-tbody">
            {loading && (
              <tr><td colSpan={10} className="p-6 text-center text-neutral-400" data-testid="history-loading">Caricamento…</td></tr>
            )}
            {!loading && counts.length === 0 && (
              <tr><td colSpan={10} className="p-6 text-center text-neutral-400" data-testid="history-empty">
                Nessun inventario fisico registrato. Premi "Nuovo inventario" per iniziare.
              </td></tr>
            )}
            {counts.map((c) => (
              <tr key={c.id} className="border-t border-[#E2D9C8] hover:bg-[#FDFBF7] cursor-pointer" data-testid={`history-row-${c.id}`} onClick={() => onOpen(c.id)}>
                <td className="p-3 whitespace-nowrap">{fmtDate(c.started_at)}</td>
                <td className="p-3">{c.name}</td>
                <td className="p-3"><StateBadge s={c.state} /></td>
                <td className="p-3 text-xs text-neutral-500">{c.started_by || "—"}</td>
                <td className="p-3 text-xs text-neutral-500">{c.completed_by || "—"}</td>
                <td className="p-3 text-right">{fmtEUR(c.theoretical_value)}</td>
                <td className="p-3 text-right">{c.real_value == null ? "—" : fmtEUR(c.real_value)}</td>
                <td className={`p-3 text-right ${c.diff_value == null ? "text-neutral-400" : c.diff_value < 0 ? "text-red-700" : "text-emerald-800"}`}>
                  {c.diff_value == null ? "—" : fmtEUR(c.diff_value)}
                </td>
                <td className="p-3 text-right">{c.diff_count ?? 0}</td>
                <td className="p-3 text-right">
                  <Button variant="outline" size="sm" data-testid={`open-${c.id}`}>Apri</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- DIFFERENCE REPORT ----------
function DifferenceReport() {
  const [rows, setRows] = useState(null);
  const [top, setTop] = useState(20);
  useEffect(() => {
    let alive = true;
    api.get(`/inventory-counts/report/differences?top=${top}`)
      .then(({ data }) => { if (alive) setRows(data.items || []); })
      .catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, [top]);
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-neutral-500">Articoli con maggiori differenze cumulative</div>
        <Select value={String(top)} onValueChange={(v) => setTop(Number(v))}>
          <SelectTrigger className="w-32" data-testid="report-top-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="10">Top 10</SelectItem>
            <SelectItem value="20">Top 20</SelectItem>
            <SelectItem value="50">Top 50</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="border border-[#E2D9C8] rounded-lg bg-white overflow-x-auto">
        <table className="w-full text-sm zebra min-w-[900px]">
          <thead><tr className="text-left label-eyebrow">
            <th className="p-3">Articolo</th>
            <th className="p-3">Unità</th>
            <th className="p-3 text-right">Diff. qtà cumulativa</th>
            <th className="p-3 text-right">Diff. € cumulativa</th>
            <th className="p-3 text-right">Freq. differenze</th>
          </tr></thead>
          <tbody data-testid="report-tbody">
            {rows === null && <tr><td colSpan={5} className="p-6 text-center text-neutral-400">Caricamento…</td></tr>}
            {rows && rows.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-neutral-400" data-testid="report-empty">Nessun dato — completa almeno un inventario con differenze.</td></tr>}
            {rows && rows.map((r) => (
              <tr key={r.inventory_id} className="border-t border-[#E2D9C8]">
                <td className="p-3">{r.name}</td>
                <td className="p-3 text-xs text-neutral-500">{r.unit || "—"}</td>
                <td className={`p-3 text-right ${r.diff_qty < 0 ? "text-red-700" : "text-emerald-800"}`}>{Number(r.diff_qty || 0).toFixed(2)}</td>
                <td className={`p-3 text-right ${r.diff_value < 0 ? "text-red-700" : "text-emerald-800"}`}>{fmtEUR(r.diff_value)}</td>
                <td className="p-3 text-right">{r.counts_with_diff}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- NEW DIALOG ----------
function NewInventoryDialog({ open, onOpenChange, onCreated }) {
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/inventory-counts", { name: name || undefined, notes });
      toast.success("Inventario creato — snapshot congelato");
      onOpenChange(false);
      setName(""); setNotes("");
      onCreated(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore creazione inventario");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="new-inv-dialog">
        <DialogHeader><DialogTitle>Nuovo inventario fisico</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <div className="label-eyebrow mb-1">Nome (opzionale)</div>
            <Input data-testid="new-inv-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Es. Inventario mensile Marzo" />
          </div>
          <div>
            <div className="label-eyebrow mb-1">Note</div>
            <Textarea data-testid="new-inv-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
          </div>
          <div className="text-xs text-neutral-500">
            Al conferma le giacenze teoriche verranno "congelate" e non cambieranno anche se registri altre vendite durante il conteggio.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Annulla</Button>
          <Button data-testid="new-inv-submit" onClick={submit} disabled={busy} className="bg-neutral-900 text-[#FDFBF7]">
            {busy ? "Creazione…" : "Crea e inizia conteggio"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- DETAIL (COUNT / REVIEW / COMPLETED) ----------
function CountDetail({ countId, onClose }) {
  const [doc, setDoc] = useState(null);
  const [values, setValues] = useState({});   // inventory_id -> string
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [onlyPending, setOnlyPending] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [tab, setTab] = useState("count");
  const confirmLock = useRef(false);
  const saveTimer = useRef(null);
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/inventory-counts/${countId}`);
    setDoc(data);
    // seed values from server (real_qty already saved) but keep any local dirty entries
    setValues((prev) => {
      const next = {};
      for (const l of data.lines || []) {
        if (prev[l.inventory_id] !== undefined && prev[l.inventory_id] !== "") next[l.inventory_id] = prev[l.inventory_id];
        else next[l.inventory_id] = l.real_qty == null ? "" : String(l.real_qty);
      }
      return next;
    });
    if (data.state === "completed" || data.state === "cancelled") setTab("review");
    else if (data.state === "review") setTab("review");
  };

  useEffect(() => { load(); }, [countId]);

  const categories = useMemo(() => {
    if (!doc) return [];
    return Array.from(new Set(doc.lines.map((l) => l.category).filter(Boolean))).sort();
  }, [doc]);

  const filteredLines = useMemo(() => {
    if (!doc) return [];
    let ls = doc.lines;
    if (cat !== "all") ls = ls.filter((l) => l.category === cat);
    if (q.trim()) {
      const qq = q.toLowerCase();
      ls = ls.filter((l) => (l.name || "").toLowerCase().includes(qq));
    }
    if (onlyPending) ls = ls.filter((l) => (values[l.inventory_id] ?? "") === "");
    return ls;
  }, [doc, cat, q, onlyPending, values]);

  // aggregates use LIVE values (typed but not yet saved included)
  const aggregates = useMemo(() => {
    if (!doc) return { real: 0, diff: 0, diffCount: 0, pending: 0, withDiff: 0 };
    let real = 0, diff = 0, diffCount = 0, pending = 0;
    for (const l of doc.lines) {
      const v = values[l.inventory_id];
      const hasVal = v !== "" && v !== undefined && v !== null && !Number.isNaN(Number(v));
      if (!hasVal) { pending += 1; real += Number(l.theoretical_qty) * Number(l.unit_cost); continue; }
      const rq = Number(v);
      real += rq * Number(l.unit_cost);
      const d = rq - Number(l.theoretical_qty);
      if (Math.abs(d) > 1e-9) { diffCount += 1; diff += d * Number(l.unit_cost); }
    }
    return { real, diff, diffCount, pending, withDiff: diffCount };
  }, [doc, values]);

  const readonly = doc && (doc.state === "completed" || doc.state === "cancelled");

  const buildLines = () => {
    return (doc?.lines || []).map((l) => {
      const v = values[l.inventory_id];
      const hasVal = v !== "" && v !== undefined && v !== null && !Number.isNaN(Number(v));
      return hasVal ? { inventory_id: l.inventory_id, real_qty: Number(v) } : { inventory_id: l.inventory_id, real_qty: null };
    });
  };

  const persist = async (state = null) => {
    if (readonly) return;
    setSaving(true);
    try {
      const body = { lines: buildLines() };
      if (state) body.state = state;
      const { data } = await api.put(`/inventory-counts/${countId}`, body);
      setDoc(data);
      setDirty(false);
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    } finally { setSaving(false); }
  };

  // progressive autosave (debounced 1.5s)
  const scheduleAutosave = () => {
    setDirty(true);
    if (readonly) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => { persist(); }, 1500);
  };

  useEffect(() => () => { if (saveTimer.current) clearTimeout(saveTimer.current); }, []);

  const goReview = async () => {
    await persist("review");
    setTab("review");
  };

  const confirm = async () => {
    if (confirmLock.current) return;
    if (readonly) { toast.info("Inventario già chiuso"); return; }
    if (aggregates.pending > 0) {
      const ok = window.confirm(
        `${aggregates.pending} articoli non conteggiati.\nSaranno considerati con quantità = teorica (nessuna rettifica).\nProcedere comunque?`,
      );
      if (!ok) return;
    }
    confirmLock.current = true;
    setConfirming(true);
    try {
      await persist(); // save latest values first
      const { data } = await api.post(`/inventory-counts/${countId}/confirm`);
      toast.success(`Inventario confermato — ${data.applied} rettifiche applicate`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore conferma");
    } finally {
      setConfirming(false);
      // keep lock for 2s to absorb double-click
      setTimeout(() => { confirmLock.current = false; }, 2000);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Annullare questo inventario? Le quantità di magazzino non verranno modificate.")) return;
    try {
      await api.post(`/inventory-counts/${countId}/cancel`);
      toast.success("Inventario annullato");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore annullamento");
    }
  };

  if (!doc) return <div className="p-6 text-neutral-500" data-testid="count-loading">Caricamento inventario…</div>;

  return (
    <div data-testid="count-detail">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="text-xs text-neutral-500">
            <button className="underline" onClick={onClose} data-testid="back-to-history">← Torna allo storico</button>
          </div>
          <h2 className="font-serif text-3xl mt-1">{doc.name}</h2>
          <div className="text-xs text-neutral-500 mt-1">
            Aperto {fmtDate(doc.started_at)} da <b>{doc.started_by}</b>
            {doc.completed_at && <> · Chiuso {fmtDate(doc.completed_at)} da <b>{doc.completed_by}</b></>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StateBadge s={doc.state} />
          {saving && <span className="text-xs text-neutral-500" data-testid="autosave-indicator">Salvataggio…</span>}
          {!saving && dirty && <span className="text-xs text-amber-700">Modifiche non salvate</span>}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        <StatCard testid="stat-theo" label="Valore teorico" value={fmtEUR(doc.theoretical_value)} />
        <StatCard testid="stat-real" label="Valore reale" value={fmtEUR(aggregates.real)} />
        <StatCard testid="stat-diff" label="Differenza €" value={fmtEUR(aggregates.real - doc.theoretical_value)} tone={(aggregates.real - doc.theoretical_value) < 0 ? "danger" : "good"} />
        <StatCard testid="stat-withdiff" label="Articoli con diff." value={String(aggregates.withDiff)} />
        <StatCard testid="stat-pending" label="Non conteggiati" value={String(aggregates.pending)} tone={aggregates.pending > 0 ? "danger" : "good"} />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="count" data-testid="tab-count" disabled={readonly}>1. Conteggio</TabsTrigger>
          <TabsTrigger value="review" data-testid="tab-review">2. Revisione</TabsTrigger>
        </TabsList>

        <TabsContent value="count">
          <div className="flex flex-wrap gap-2 my-4">
            <Input data-testid="search-input" placeholder="Cerca articolo…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs" />
            <Select value={cat} onValueChange={setCat}>
              <SelectTrigger className="w-56" data-testid="cat-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le categorie</SelectItem>
                {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant={onlyPending ? "default" : "outline"} onClick={() => setOnlyPending((x) => !x)} data-testid="filter-pending">
              {onlyPending ? "Mostra tutti" : `Solo non conteggiati (${aggregates.pending})`}
            </Button>
            <div className="flex-1" />
            <Button variant="outline" onClick={() => persist()} disabled={saving || readonly} data-testid="save-progress">
              {saving ? "Salvataggio…" : "Salva progresso"}
            </Button>
            <Button onClick={goReview} disabled={saving || readonly} className="bg-neutral-900 text-[#FDFBF7]" data-testid="goto-review">
              Vai a revisione →
            </Button>
          </div>

          <div className="border border-[#E2D9C8] rounded-lg bg-white overflow-x-auto">
            <table className="w-full text-sm zebra min-w-[900px]">
              <thead><tr className="text-left label-eyebrow">
                <th className="p-3">Articolo</th>
                <th className="p-3">Categoria</th>
                <th className="p-3">Unità</th>
                <th className="p-3 text-right">Qtà teorica</th>
                <th className="p-3 text-right w-40">Qtà reale</th>
                <th className="p-3 text-right">Δ qtà</th>
              </tr></thead>
              <tbody data-testid="count-tbody">
                {filteredLines.length === 0 && (
                  <tr><td colSpan={6} className="p-6 text-center text-neutral-400">Nessun articolo corrispondente.</td></tr>
                )}
                {filteredLines.map((l) => {
                  const v = values[l.inventory_id] ?? "";
                  const hasVal = v !== "" && v !== null && v !== undefined && !Number.isNaN(Number(v));
                  const d = hasVal ? Number(v) - Number(l.theoretical_qty) : null;
                  return (
                    <tr key={l.inventory_id} className="border-t border-[#E2D9C8]">
                      <td className="p-3">{l.name}</td>
                      <td className="p-3 text-xs text-neutral-500">{l.category || "—"}</td>
                      <td className="p-3 text-xs text-neutral-500">{l.unit || "—"}</td>
                      <td className="p-3 text-right">{Number(l.theoretical_qty).toFixed(2)}</td>
                      <td className="p-3 text-right">
                        <Input
                          data-testid={`count-input-${l.inventory_id}`}
                          type="number"
                          step="0.01"
                          value={v}
                          disabled={readonly}
                          onChange={(e) => { setValues((s) => ({ ...s, [l.inventory_id]: e.target.value })); scheduleAutosave(); }}
                          onBlur={() => { if (saveTimer.current) clearTimeout(saveTimer.current); persist(); }}
                          className="text-right"
                        />
                      </td>
                      <td className={`p-3 text-right ${d === null ? "text-neutral-400" : d < 0 ? "text-red-700" : d > 0 ? "text-emerald-800" : ""}`}>
                        {d === null ? "—" : d.toFixed(2)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="review">
          <div className="my-4 flex justify-between flex-wrap gap-2">
            <div className="text-sm text-neutral-500">Verifica differenze e conferma per generare le rettifiche di magazzino.</div>
            <div className="flex gap-2">
              {!readonly && <Button variant="outline" onClick={() => setTab("count")} data-testid="back-to-count">← Torna al conteggio</Button>}
              {!readonly && <Button variant="outline" onClick={cancel} data-testid="cancel-inv">Annulla inventario</Button>}
              {!readonly && (
                <Button
                  onClick={confirm}
                  disabled={confirming || saving}
                  className="bg-emerald-900 text-white"
                  data-testid="confirm-inv"
                >
                  {confirming ? "Conferma in corso…" : "Conferma & applica rettifiche"}
                </Button>
              )}
            </div>
          </div>

          <div className="border border-[#E2D9C8] rounded-lg bg-white overflow-x-auto">
            <table className="w-full text-sm zebra min-w-[900px]">
              <thead><tr className="text-left label-eyebrow">
                <th className="p-3">Articolo</th>
                <th className="p-3">Unità</th>
                <th className="p-3 text-right">Teorica</th>
                <th className="p-3 text-right">Reale</th>
                <th className="p-3 text-right">Δ qtà</th>
                <th className="p-3 text-right">Δ %</th>
                <th className="p-3 text-right">Costo unit.</th>
                <th className="p-3 text-right">Diff €</th>
              </tr></thead>
              <tbody data-testid="review-tbody">
                {doc.lines.map((l) => {
                  const v = values[l.inventory_id];
                  const hasVal = v !== "" && v !== undefined && v !== null && !Number.isNaN(Number(v));
                  const rq = hasVal ? Number(v) : null;
                  const tq = Number(l.theoretical_qty);
                  const d = rq === null ? null : rq - tq;
                  const dPct = d === null || tq === 0 ? null : (d / tq) * 100;
                  const dEur = d === null ? null : d * Number(l.unit_cost);
                  const isDiff = d !== null && Math.abs(d) > 1e-9;
                  const isPending = rq === null;
                  return (
                    <tr key={l.inventory_id} className={`border-t border-[#E2D9C8] ${isPending ? "bg-neutral-50" : ""}`}>
                      <td className="p-3">
                        {l.name}
                        {isPending && <Badge className="ml-2 bg-neutral-200 text-neutral-700">non conteggiato</Badge>}
                      </td>
                      <td className="p-3 text-xs text-neutral-500">{l.unit || "—"}</td>
                      <td className="p-3 text-right">{tq.toFixed(2)}</td>
                      <td className="p-3 text-right">{rq === null ? "—" : rq.toFixed(2)}</td>
                      <td className={`p-3 text-right ${d === null ? "text-neutral-400" : d < 0 ? "text-red-700" : d > 0 ? "text-emerald-800" : ""}`}>
                        {d === null ? "—" : d.toFixed(2)}
                      </td>
                      <td className={`p-3 text-right text-xs ${dPct === null ? "text-neutral-400" : dPct < 0 ? "text-red-700" : dPct > 0 ? "text-emerald-800" : ""}`}>
                        {dPct === null ? "—" : `${dPct.toFixed(1)}%`}
                      </td>
                      <td className="p-3 text-right text-xs text-neutral-500">{fmtEUR(l.unit_cost)}</td>
                      <td className={`p-3 text-right font-medium ${dEur === null ? "text-neutral-400" : dEur < 0 ? "text-red-700" : dEur > 0 ? "text-emerald-800" : ""}`}>
                        {dEur === null ? "—" : fmtEUR(dEur)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-[#8B7355] bg-[#FDFBF7] font-semibold">
                  <td className="p-3" colSpan={2}>Totale</td>
                  <td className="p-3 text-right">{fmtEUR(doc.theoretical_value)}</td>
                  <td className="p-3 text-right">{fmtEUR(aggregates.real)}</td>
                  <td className="p-3" colSpan={3}></td>
                  <td className={`p-3 text-right ${(aggregates.real - doc.theoretical_value) < 0 ? "text-red-700" : "text-emerald-800"}`}>
                    {fmtEUR(aggregates.real - doc.theoretical_value)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {doc.state === "completed" && (
            <div className="mt-4 p-4 border border-emerald-300 bg-emerald-50 rounded-lg text-sm text-emerald-900" data-testid="completed-banner">
              ✓ Inventario completato — {doc.applied_movements ?? doc.diff_count ?? 0} rettifiche applicate al magazzino il {fmtDate(doc.completed_at)}.
            </div>
          )}
          {doc.state === "cancelled" && (
            <div className="mt-4 p-4 border border-neutral-300 bg-neutral-50 rounded-lg text-sm text-neutral-700" data-testid="cancelled-banner">
              Inventario annullato — nessuna rettifica applicata.
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ---------- MAIN ----------
export default function InventoryCount() {
  const [tab, setTab] = useState("history");
  const [counts, setCounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [newOpen, setNewOpen] = useState(false);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/inventory-counts");
      setCounts(data || []);
    } catch (e) {
      toast.error("Errore caricamento storico");
      setCounts([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { loadHistory(); }, []);

  const openCount = (id) => setActiveId(id);
  const closeCount = () => { setActiveId(null); loadHistory(); };

  return (
    <div data-testid="inventory-count-page">
      <PageHeader
        eyebrow="Inventario fisico"
        title="Conta reale"
        subtitle="Rileva le giacenze reali, confronta con la teorica e applica le rettifiche di magazzino."
      />

      <NewInventoryDialog
        open={newOpen}
        onOpenChange={setNewOpen}
        onCreated={(doc) => { setActiveId(doc.id); loadHistory(); }}
      />

      {activeId ? (
        <CountDetail countId={activeId} onClose={closeCount} />
      ) : (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="history" data-testid="tab-history">Storico</TabsTrigger>
            <TabsTrigger value="report" data-testid="tab-report">Report differenze</TabsTrigger>
          </TabsList>
          <TabsContent value="history">
            <HistoryList counts={counts} loading={loading} onOpen={openCount} onNew={() => setNewOpen(true)} />
          </TabsContent>
          <TabsContent value="report">
            <DifferenceReport />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
